import i18n from "../i18n";
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableHead, TableHeader, TableRow, TableCell, TableBody } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Wallet, Plus, TrendingUp, TrendingDown, HardHat, Download, FileSpreadsheet, Landmark, Receipt, Users } from "lucide-react";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/format";
import { farmDateIso } from "@/lib/farmTime";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, PieChart, Pie, Cell, Legend } from "recharts";

const INCOME_CATEGORIES = ["animal_sales", "feed_sales", "product_sales", "egg_sales", "meat_sales", "crop_sales", "other_income"];
const EXPENSE_CATEGORIES = ["feed", "medicine", "vaccination", "veterinary", "fuel", "electricity", "water", "payroll", "repairs", "equipment", "miscellaneous", "capital_expenses"];
const CHART_COLORS = ["#40916c", "#7f5539", "#e07a5f", "#3d5a80", "#2a9d8f", "#f4a261", "#6c757d"];

export default function FinancialPage() {
    const [items, setItems] = useState([]);
    const [capex, setCapex] = useState([]);
    const [projects, setProjects] = useState([]);
    const [farm, setFarm] = useState({ currency: "PHP" });
    const [dlg, setDlg] = useState(null);
    const [supplierPayDlg, setSupplierPayDlg] = useState(null);
    const [summary, setSummary] = useState(null);
    const [periodType, setPeriodType] = useState("monthly");
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const load = async () => {
        setLoading(true); setError("");
        try {
            const [t, e, p, f, s] = await Promise.all([
                api.get("/transactions").then(r => r.data),
                api.get("/project-expenses").then(r => r.data),
                api.get("/construction-projects").then(r => r.data),
                api.get("/farm").then(r => r.data),
                api.get(`/financial/summary?period_type=${periodType}`).then(r => r.data),
            ]);
            setItems(t); setCapex(e); setProjects(p); setFarm(f || { currency: "PHP" }); setSummary(s);
        } catch (requestError) {
            setError(requestError.response?.status === 403 ? "You are not authorized to view financial records." : "Financial records could not be loaded. No totals are being shown.");
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [periodType]);

    const save = async () => {
        if (saving) return;
        setSaving(true);
        try {
            const body = { ...dlg, amount: Number(dlg.amount) };
            if (dlg.id) await api.put(`/transactions/${dlg.id}`, body); else await api.post("/transactions", body);
            toast.success("Saved"); setDlg(null); await load();
        } finally { setSaving(false); }
    };
    const del = async (it) => { if (window.confirm("Delete?")) { await api.delete(`/transactions/${it.id}`); load(); } };

    const saveSupplierPayment = async () => {
        if (saving) return;
        setSaving(true);
        const body = {
            purchase_order_id: supplierPayDlg.purchase_order_id || "",
            supplier: supplierPayDlg.supplier || "",
            amount: Number(supplierPayDlg.amount || 0),
            date: supplierPayDlg.date,
            payment_method: supplierPayDlg.payment_method || "cash",
            description: supplierPayDlg.description || "",
            notes: supplierPayDlg.notes || "",
            idempotency_key: supplierPayDlg.idempotency_key,
        };
        try {
            await api.post("/financial/supplier-payments", body);
            toast.success("Supplier payment saved");
            setSupplierPayDlg(null);
            await load();
        } finally { setSaving(false); }
    };

    const projectMap = useMemo(() => Object.fromEntries(projects.map(p => [p.id, p])), [projects]);
    const purchaseOrders = summary?.accounts_payable?.rows || [];

    const income = Number(summary?.income?.total || 0);
    const opex = Number(summary?.expenses?.total || 0);
    const capexTotal = Number(summary?.expenses?.capital_expenses || 0);
    const operatingExpense = Number(summary?.expenses?.operating_total ?? opex - capexTotal);
    const opProfit = Number(summary?.profit_and_loss?.gross_profit ?? income - operatingExpense);
    const netProfit = Number(summary?.profit_and_loss?.net_profit ?? income - opex);

    // Merged rows: transactions + project expenses (as read-only "construction" rows)
    const rows = useMemo(() => {
        const txRows = items.map(t => ({ ...t, _source: "tx" }));
        const capRows = capex.map(e => ({
            id: `capex-${e.id}`,
            _source: "capex",
            _capexId: e.id,
            date: e.date,
            type: "capex",
            category: e.category || "construction",
            description: `${projectMap[e.project_id]?.name || "Project"} — ${e.description || ""}`.trim(),
            amount: e.amount,
        }));
        return [...txRows, ...capRows].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    }, [items, capex, projectMap]);

    const cur = (value) => formatCurrency(value || 0, farm.currency);

    const incomeChartData = useMemo(() => {
        if (!summary?.income) return [];
        return INCOME_CATEGORIES.map((key) => ({ name: key.replace(/_/g, " "), value: Number(summary.income[key] || 0) })).filter((row) => row.value > 0);
    }, [summary]);

    const expenseChartData = useMemo(() => {
        if (!summary?.expenses) return [];
        return EXPENSE_CATEGORIES.map((key) => ({ name: key.replace(/_/g, " "), value: Number(summary.expenses[key] || 0) })).filter((row) => row.value > 0);
    }, [summary]);

    if (loading) return <div className="space-y-4" data-testid="financial-page"><Card className="p-6 text-muted-foreground" data-testid="financial-loading-state">{i18n.t("batch3.16e890a4714a")}</Card></div>;
    if (error) return <div className="space-y-4" data-testid="financial-page"><Card className="p-4 border-destructive/40 text-destructive" role="alert" data-testid="financial-error-state">{error}</Card><Button variant="outline" onClick={load}>{i18n.t("batch3.042c862e4467")}</Button></div>;

    const exportExcel = async () => {
        if (!summary) return;
        const XLSX = await import("xlsx");
        const wb = XLSX.utils.book_new();
        const sheets = {
            Summary: [{
                period_start: summary.period?.start,
                period_end: summary.period?.end,
                income_total: summary.income?.total,
                expense_total: summary.expenses?.total,
                net_profit: summary.profit_and_loss?.net_profit,
                receivable: summary.accounts_receivable?.total,
                payable: summary.accounts_payable?.total,
                inventory_value: summary.inventory?.value,
            }],
            Receivables: summary.accounts_receivable?.rows || [],
            Payables: summary.accounts_payable?.rows || [],
            CustomerPayments: summary.customer_payments?.rows || [],
            SupplierPayments: summary.supplier_payments?.rows || [],
            Transactions: rows,
        };
        Object.entries(sheets).forEach(([name, data]) => {
            const flat = (data || []).map((row) => Object.fromEntries(Object.entries(row || {}).map(([key, value]) => [key, typeof value === "object" && value !== null ? JSON.stringify(value) : value])));
            const ws = XLSX.utils.json_to_sheet(flat);
            XLSX.utils.book_append_sheet(wb, ws, name.slice(0, 31));
        });
        const buf = XLSX.write(wb, { type: "array", bookType: "xlsx" });
        const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `financial-summary-${summary.period?.start || "current"}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const exportPdf = () => {
        if (!summary) return;
        const doc = new jsPDF();
        doc.setFontSize(18);
        doc.text("Financial Summary", 14, 18);
        doc.setFontSize(10);
        doc.text(`${summary.period?.start || ""} to ${summary.period?.end || ""}`, 14, 24);
        autoTable(doc, {
            startY: 32,
            head: [["Metric", "Amount"]],
            body: [
                ["Income", cur(summary.income?.total)],
                ["Expenses", cur(summary.expenses?.total)],
                ["Net Profit", cur(summary.profit_and_loss?.net_profit)],
                ["Accounts Receivable", cur(summary.accounts_receivable?.total)],
                ["Accounts Payable", cur(summary.accounts_payable?.total)],
                ["Inventory Value", cur(summary.inventory?.value)],
            ],
        });
        autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 8,
            head: [["Customer", "Invoice", "Outstanding"]],
            body: (summary.accounts_receivable?.rows || []).slice(0, 10).map((row) => [row.customer_name || "—", row.invoice_number || row.invoice_id || "—", cur(row.outstanding)]),
        });
        doc.save(`financial-summary-${summary.period?.start || "current"}.pdf`);
    };

    return (
        <div className="space-y-6" data-testid="financial-page">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Wallet className="w-8 h-8 text-primary" />
                    <div>
                        <div className="tiny-label">{i18n.t("batch3.1dc043bb8eb5")}</div>
                        <h1 className="text-4xl font-bold tracking-tight">{i18n.t("batch3.bd91f0161b70")}</h1>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Select value={periodType} onValueChange={setPeriodType}>
                        <SelectTrigger className="h-12 w-36"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="daily">{i18n.t("batch3.728298d3dbf4")}</SelectItem>
                            <SelectItem value="weekly">{i18n.t("batch3.158f3da59275")}</SelectItem>
                            <SelectItem value="monthly">{i18n.t("batch3.d31edb7b8a94")}</SelectItem>
                            <SelectItem value="annual">{i18n.t("batch3.0930f3f8992c")}</SelectItem>
                        </SelectContent>
                    </Select>
                    <Button variant="outline" className="h-12" onClick={exportExcel}><FileSpreadsheet className="w-4 h-4 mr-2" />{i18n.t("batch3.a08e11119aef")}</Button>
                    <Button variant="outline" className="h-12" onClick={exportPdf}><Download className="w-4 h-4 mr-2" />{i18n.t("batch3.d613d88cb2d8")}</Button>
                    <Dialog open={!!supplierPayDlg} onOpenChange={(open) => !open && setSupplierPayDlg(null)}>
                        <DialogTrigger asChild>
                            <Button variant="outline" className="h-12" onClick={() => setSupplierPayDlg({ purchase_order_id: purchaseOrders[0]?.purchase_order_id || "", supplier: purchaseOrders[0]?.supplier || "", amount: 0, date: farmDateIso(), payment_method: "cash", description: "", notes: "", idempotency_key: crypto.randomUUID() })}>{i18n.t("batch3.706b2285196c")}</Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader><DialogTitle>{i18n.t("batch3.706b2285196c")}</DialogTitle></DialogHeader>
                            {supplierPayDlg ? <div className="space-y-4">
                                <div><Label className="tiny-label">{i18n.t("batch3.3c45b957fdc8")}</Label>
                                    <Select value={supplierPayDlg.purchase_order_id} onValueChange={(value) => {
                                        const row = purchaseOrders.find((entry) => entry.purchase_order_id === value);
                                        setSupplierPayDlg({ ...supplierPayDlg, purchase_order_id: value, supplier: row?.supplier || supplierPayDlg.supplier });
                                    }}>
                                        <SelectTrigger className="h-12 mt-2"><SelectValue placeholder={i18n.t("batch3.30e4e2a86cc2")} /></SelectTrigger>
                                        <SelectContent>{purchaseOrders.map((row) => <SelectItem key={row.purchase_order_id} value={row.purchase_order_id}>{`${row.supplier} · ${cur(row.outstanding)}`}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                <div><Label className="tiny-label">{i18n.t("batch3.55edd462872a")}</Label><Input value={supplierPayDlg.supplier} onChange={(e) => setSupplierPayDlg({ ...supplierPayDlg, supplier: e.target.value })} className="h-12 mt-2" /></div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div><Label className="tiny-label">{i18n.t("batch3.43dc8532f7e5")}</Label><Input type="number" value={supplierPayDlg.amount} onChange={(e) => setSupplierPayDlg({ ...supplierPayDlg, amount: e.target.value })} className="h-12 mt-2" /></div>
                                    <div><Label className="tiny-label">{i18n.t("batch3.eb9a4bc1c0c1")}</Label><Input type="date" value={supplierPayDlg.date} onChange={(e) => setSupplierPayDlg({ ...supplierPayDlg, date: e.target.value })} className="h-12 mt-2" /></div>
                                </div>
                                <div><Label className="tiny-label">{i18n.t("batch3.55f8ebc805e6")}</Label><Input value={supplierPayDlg.description} onChange={(e) => setSupplierPayDlg({ ...supplierPayDlg, description: e.target.value })} className="h-12 mt-2" /></div>
                                <div><Label className="tiny-label">{i18n.t("batch3.70440046a3dc")}</Label><Textarea value={supplierPayDlg.notes} onChange={(e) => setSupplierPayDlg({ ...supplierPayDlg, notes: e.target.value })} /></div>
                                <Button onClick={saveSupplierPayment} disabled={saving} className="w-full">{i18n.t("batch3.5ab37b321059")}</Button>
                            </div> : null}
                        </DialogContent>
                    </Dialog>
                    <Dialog open={!!dlg} onOpenChange={o => !o && setDlg(null)}>
                        <DialogTrigger asChild>
                            <Button className="h-12" onClick={() => setDlg({ type: "income", category: "animal_sales", amount: 0, description: "", date: farmDateIso(), idempotency_key: crypto.randomUUID() })} data-testid="add-transaction-btn">
                                <Plus className="w-5 h-5 mr-2" /> {i18n.t("batch3.cfc68d1ba331")}
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader><DialogTitle>{dlg?.id ? "Edit" : "New"} {i18n.t("batch3.1a7b7c1b33d1")}</DialogTitle></DialogHeader>
                            {dlg && <div className="space-y-4">
                                <div><Label className="tiny-label">{i18n.t("batch3.3deb74565196")}</Label>
                                    <Select value={dlg.type} onValueChange={v => setDlg({ ...dlg, type: v, category: v === "income" ? "animal_sales" : "feed" })}>
                                        <SelectTrigger className="h-12 mt-2" data-testid="tx-type-select"><SelectValue /></SelectTrigger>
                                        <SelectContent><SelectItem value="income">{i18n.t("batch3.1c89b1f2aaab")}</SelectItem><SelectItem value="expense">{i18n.t("batch3.a0db8e68b834")}</SelectItem></SelectContent>
                                    </Select>
                                </div>
                                <div><Label className="tiny-label">{i18n.t("batch3.a3c686e711e4")}</Label>
                                    <Select value={dlg.category} onValueChange={(value) => setDlg({ ...dlg, category: value })}>
                                        <SelectTrigger className="h-12 mt-2" data-testid="tx-category-input"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {(dlg.type === "income" ? INCOME_CATEGORIES : EXPENSE_CATEGORIES).map((entry) => <SelectItem key={entry} value={entry}>{entry.replace(/_/g, " ")}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div><Label className="tiny-label">{i18n.t("batch3.43dc8532f7e5")}</Label><Input type="number" value={dlg.amount} onChange={e=>setDlg({...dlg,amount:e.target.value})} className="h-12 mt-2" data-testid="tx-amount-input"/></div>
                                <div><Label className="tiny-label">{i18n.t("batch3.eb9a4bc1c0c1")}</Label><Input type="date" value={dlg.date} onChange={e=>setDlg({...dlg,date:e.target.value})} className="h-12 mt-2"/></div>
                                <div><Label className="tiny-label">{i18n.t("batch3.55f8ebc805e6")}</Label><Textarea value={dlg.description} onChange={e=>setDlg({...dlg,description:e.target.value})} /></div>
                                <Button onClick={save} disabled={saving} className="h-12 w-full" data-testid="save-tx-btn">{i18n.t("batch3.efc007a393f6")}</Button>
                            </div>}
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 xl:grid-cols-8 gap-4">
                <Card className="p-6"><div className="tiny-label flex items-center gap-2"><TrendingUp className="w-4 h-4 text-primary" /> {i18n.t("batch3.1c89b1f2aaab")}</div><div className="kpi-value text-3xl font-bold mt-3 text-primary">{formatCurrency(income, farm.currency)}</div></Card>
                <Card className="p-6"><div className="tiny-label flex items-center gap-2"><TrendingDown className="w-4 h-4 text-accent" /> {i18n.t("batch3.052935803586")}</div><div className="kpi-value text-3xl font-bold mt-3" style={{color: "hsl(var(--accent))"}}>{formatCurrency(operatingExpense, farm.currency)}</div></Card>
                <Card className="p-6"><div className="tiny-label flex items-center gap-2"><HardHat className="w-4 h-4 text-amber-600" /> {i18n.t("batch3.e7122e2a7891")}</div><div className="kpi-value text-3xl font-bold mt-3 text-amber-700" data-testid="financial-capex-kpi">{formatCurrency(capexTotal, farm.currency)}</div><div className="text-xs text-muted-foreground mt-1">{i18n.t("batch3.4af592953912")}</div></Card>
                <Card className="p-6"><div className="tiny-label">{i18n.t("batch3.d1f0809a0d18")}</div><div className={`kpi-value text-3xl font-bold mt-3 ${opProfit >= 0 ? "text-primary" : "text-destructive"}`}>{formatCurrency(opProfit, farm.currency)}</div></Card>
                <Card className="p-6"><div className="tiny-label">{i18n.t("batch3.15c19e1d9e1e")}</div><div className={`kpi-value text-3xl font-bold mt-3 ${netProfit >= 0 ? "text-primary" : "text-destructive"}`} data-testid="financial-net-profit">{formatCurrency(netProfit, farm.currency)}</div></Card>
                <Card className="p-6"><div className="tiny-label">{i18n.t("batch3.cad804aedb53")}</div><div className="kpi-value text-3xl font-bold mt-3 text-amber-700">{cur(summary?.accounts_receivable?.total)}</div></Card>
                <Card className="p-6"><div className="tiny-label">{i18n.t("batch3.8e104572372b")}</div><div className="kpi-value text-3xl font-bold mt-3 text-destructive">{cur(summary?.accounts_payable?.total)}</div></Card>
                <Card className="p-6"><div className="tiny-label">{i18n.t("batch3.d96f52b84799")}</div><div className="kpi-value text-3xl font-bold mt-3">{cur(summary?.inventory?.value)}</div></Card>
            </div>
            <div className="text-xs text-muted-foreground -mt-2">
                {i18n.t("batch3.2ee750e04190")} <a href="/overview" className="underline hover:text-primary">{i18n.t("batch3.f5f032b11074")}</a> {i18n.t("batch3.cffa50a32cb1")} <a href="/sales-dashboard" className="underline hover:text-primary">{i18n.t("batch3.e77284395aa6")}</a> {i18n.t("batch3.71fe22635ffe")}
            </div>

            {summary ? (
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                    <Card className="p-4 xl:col-span-2">
                        <h3 className="font-semibold mb-3">{i18n.t("batch3.d481afbb2502")}</h3>
                        <div style={{ minHeight: 280 }}>
                            <ResponsiveContainer width="100%" height={280}>
                                <LineChart data={summary.analytics?.income_vs_expense_trend || []}>
                                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                    <XAxis dataKey="period" fontSize={11} />
                                    <YAxis fontSize={11} />
                                    <RTooltip formatter={(value) => cur(value)} />
                                    <Legend />
                                    <Line type="monotone" dataKey="income" stroke="#40916c" strokeWidth={2} />
                                    <Line type="monotone" dataKey="expense" stroke="#e07a5f" strokeWidth={2} />
                                    <Line type="monotone" dataKey="net" stroke="#3d5a80" strokeWidth={2} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </Card>
                    <Card className="p-4">
                        <h3 className="font-semibold mb-3">{i18n.t("batch3.5fc7a471696c")}</h3>
                        <div style={{ minHeight: 280 }}>
                            <ResponsiveContainer width="100%" height={280}>
                                <PieChart>
                                    <Pie data={incomeChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                                        {incomeChartData.map((row, index) => <Cell key={row.name} fill={CHART_COLORS[index % CHART_COLORS.length]} />)}
                                    </Pie>
                                    <RTooltip formatter={(value) => cur(value)} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </Card>
                    <Card className="p-4">
                        <h3 className="font-semibold mb-3">{i18n.t("batch3.60d582bbb8cd")}</h3>
                        <div style={{ minHeight: 280 }}>
                            <ResponsiveContainer width="100%" height={280}>
                                <PieChart>
                                    <Pie data={expenseChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                                        {expenseChartData.map((row, index) => <Cell key={row.name} fill={CHART_COLORS[index % CHART_COLORS.length]} />)}
                                    </Pie>
                                    <RTooltip formatter={(value) => cur(value)} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </Card>
                    <Card className="p-4">
                        <h3 className="font-semibold mb-3 flex items-center gap-2"><Landmark className="w-4 h-4" />{i18n.t("batch3.4d78e9f9edf0")}</h3>
                        <div className="space-y-2 text-sm">
                            {Object.entries(summary.cash_flow || {}).filter(([, value]) => value && typeof value === "object").map(([key, value]) => (
                                <div key={key} className="rounded border p-3">
                                    <div className="font-medium capitalize">{key.replace(/_/g, " ")}</div>
                                    <div className="text-muted-foreground text-xs">{value.start || ""}{value.end ? ` to ${value.end}` : ""}</div>
                                    <div className="mt-2 grid grid-cols-3 gap-2 font-mono text-xs">
                                        <div>{i18n.t("batch3.3c8d8b817f47")} {cur(value.inflow || 0)}</div>
                                        <div>{i18n.t("batch3.1ee8fea1697e")} {cur(value.outflow || 0)}</div>
                                        <div>{i18n.t("batch3.0bf95aa36983")} {cur(value.net || 0)}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>
                    <Card className="p-4">
                        <h3 className="font-semibold mb-3">{i18n.t("batch3.597f97a60987")}</h3>
                        <div className="space-y-2 text-sm">
                            <div className="rounded border p-3">
                                <div className="font-medium">{i18n.t("batch3.20e338624cee")}</div>
                                <div className="text-xs text-muted-foreground mt-1">{i18n.t("batch3.a16f1a3eb266")} {cur(summary.balance_sheet?.assets?.cash_and_bank)}</div>
                                <div className="text-xs text-muted-foreground">{i18n.t("batch3.dda2313a7779")} {cur(summary.balance_sheet?.assets?.accounts_receivable)}</div>
                                <div className="text-xs text-muted-foreground">{i18n.t("batch3.5f5a3fc65a12")} {cur(summary.balance_sheet?.assets?.inventory)}</div>
                                <div className="font-semibold mt-2">{i18n.t("batch3.d8e7170f94e7")} {cur(summary.balance_sheet?.assets?.total)}</div>
                            </div>
                            <div className="rounded border p-3">
                                <div className="font-medium">{i18n.t("batch3.556a420333dd")}</div>
                                <div className="text-xs text-muted-foreground mt-1">{i18n.t("batch3.14bbc03e4044")} {cur(summary.balance_sheet?.liabilities?.accounts_payable)}</div>
                                <div className="text-xs text-muted-foreground">{i18n.t("batch3.fccd53375cbe")} {cur(summary.balance_sheet?.liabilities?.payroll_payable)}</div>
                                <div className="font-semibold mt-2">{i18n.t("batch3.d8e7170f94e7")} {cur(summary.balance_sheet?.liabilities?.total)}</div>
                            </div>
                            <div className="rounded border p-3 font-semibold">{i18n.t("batch3.1ffab4400495")} {cur(summary.balance_sheet?.equity)}</div>
                        </div>
                    </Card>
                    <Card className="p-4">
                        <h3 className="font-semibold mb-3">{i18n.t("batch3.4a12196ff4e8")}</h3>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between"><span>{i18n.t("batch3.35cf82f9d2a9")}</span><span className="font-mono">{cur(summary.profit_and_loss?.revenue)}</span></div>
                            <div className="flex justify-between"><span>{i18n.t("batch3.8d437dbf95ab")}</span><span className="font-mono">{cur(summary.profit_and_loss?.expenses)}</span></div>
                            <div className="flex justify-between"><span>{i18n.t("batch3.11f53e51785e")}</span><span className="font-mono">{cur(summary.profit_and_loss?.gross_profit)}</span></div>
                            <div className="flex justify-between font-semibold"><span>{i18n.t("batch3.8bebc63cc939")}</span><span className="font-mono">{cur(summary.profit_and_loss?.net_profit)}</span></div>
                        </div>
                    </Card>
                </div>
            ) : null}

            {summary ? (
                <div className="grid gap-4 xl:grid-cols-2">
                    <Card className="p-4">
                        <h3 className="font-semibold mb-3 flex items-center gap-2"><Users className="w-4 h-4" />{i18n.t("batch3.cad804aedb53")}</h3>
                        <Table>
                            <TableHeader><TableRow><TableHead>{i18n.t("batch3.0e85749a6f40")}</TableHead><TableHead>{i18n.t("batch3.f9f38818c406")}</TableHead><TableHead>{i18n.t("batch3.145caf292855")}</TableHead><TableHead className="text-right">{i18n.t("batch3.f8ee57ec8645")}</TableHead></TableRow></TableHeader>
                            <TableBody>
                                {(summary.accounts_receivable?.rows || []).slice(0, 10).map((row) => (
                                    <TableRow key={row.invoice_id}>
                                        <TableCell>{row.customer_name || "Walk-in"}</TableCell>
                                        <TableCell className="font-mono text-xs">{row.invoice_number || row.invoice_id}</TableCell>
                                        <TableCell>{row.due_date || "—"}</TableCell>
                                        <TableCell className="text-right font-mono">{cur(row.outstanding)}</TableCell>
                                    </TableRow>
                                ))}
                                {(summary.accounts_receivable?.rows || []).length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-6 text-muted-foreground">{i18n.t("batch3.47f8ae35eabf")}</TableCell></TableRow>}
                            </TableBody>
                        </Table>
                    </Card>
                    <Card className="p-4">
                        <h3 className="font-semibold mb-3 flex items-center gap-2"><Receipt className="w-4 h-4" />{i18n.t("batch3.8e104572372b")}</h3>
                        <Table>
                            <TableHeader><TableRow><TableHead>{i18n.t("batch3.55edd462872a")}</TableHead><TableHead>{i18n.t("batch3.1d75774c0f96")}</TableHead><TableHead>{i18n.t("batch3.bae7d5be7082")}</TableHead><TableHead className="text-right">{i18n.t("batch3.f8ee57ec8645")}</TableHead></TableRow></TableHeader>
                            <TableBody>
                                {(summary.accounts_payable?.rows || []).slice(0, 10).map((row) => (
                                    <TableRow key={row.purchase_order_id}>
                                        <TableCell>{row.supplier || "—"}</TableCell>
                                        <TableCell className="font-mono text-xs">{row.purchase_order_id}</TableCell>
                                        <TableCell>{row.status || "—"}</TableCell>
                                        <TableCell className="text-right font-mono">{cur(row.outstanding)}</TableCell>
                                    </TableRow>
                                ))}
                                {(summary.accounts_payable?.rows || []).length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-6 text-muted-foreground">{i18n.t("batch3.49256255d0b0")}</TableCell></TableRow>}
                            </TableBody>
                        </Table>
                    </Card>
                    <Card className="p-4">
                        <h3 className="font-semibold mb-3">{i18n.t("batch3.14fe05d831b7")}</h3>
                        <Table>
                            <TableHeader><TableRow><TableHead>{i18n.t("batch3.eb9a4bc1c0c1")}</TableHead><TableHead>{i18n.t("batch3.0e85749a6f40")}</TableHead><TableHead>{i18n.t("batch3.88306943fea7")}</TableHead><TableHead className="text-right">{i18n.t("batch3.43dc8532f7e5")}</TableHead></TableRow></TableHeader>
                            <TableBody>
                                {(summary.customer_payments?.rows || []).slice(0, 10).map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell>{row.date || "—"}</TableCell>
                                        <TableCell>{row.customer_name || "Walk-in"}</TableCell>
                                        <TableCell>{row.method || "cash"}</TableCell>
                                        <TableCell className="text-right font-mono">{cur(row.amount)}</TableCell>
                                    </TableRow>
                                ))}
                                {(summary.customer_payments?.rows || []).length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-6 text-muted-foreground">{i18n.t("batch3.b0730eec432f")}</TableCell></TableRow>}
                            </TableBody>
                        </Table>
                    </Card>
                    <Card className="p-4">
                        <h3 className="font-semibold mb-3">{i18n.t("batch3.6ef8f5d65e5f")}</h3>
                        <Table>
                            <TableHeader><TableRow><TableHead>{i18n.t("batch3.eb9a4bc1c0c1")}</TableHead><TableHead>{i18n.t("batch3.55edd462872a")}</TableHead><TableHead>{i18n.t("batch3.db1c784524e1")}</TableHead><TableHead className="text-right">{i18n.t("batch3.43dc8532f7e5")}</TableHead></TableRow></TableHeader>
                            <TableBody>
                                {(summary.supplier_payments?.rows || []).slice(0, 10).map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell>{row.date || "—"}</TableCell>
                                        <TableCell>{row.supplier || "—"}</TableCell>
                                        <TableCell className="font-mono text-xs">{row.purchase_order_id || row.id}</TableCell>
                                        <TableCell className="text-right font-mono">{cur(row.amount)}</TableCell>
                                    </TableRow>
                                ))}
                                {(summary.supplier_payments?.rows || []).length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-6 text-muted-foreground">{i18n.t("batch3.8ab4a4daf337")}</TableCell></TableRow>}
                            </TableBody>
                        </Table>
                    </Card>
                </div>
            ) : null}

            <Card className="p-4">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{i18n.t("batch3.eb9a4bc1c0c1")}</TableHead>
                            <TableHead>{i18n.t("batch3.3deb74565196")}</TableHead>
                            <TableHead>{i18n.t("batch3.a3c686e711e4")}</TableHead>
                            <TableHead>{i18n.t("batch3.55f8ebc805e6")}</TableHead>
                            <TableHead className="text-right">{i18n.t("batch3.43dc8532f7e5")}</TableHead>
                            <TableHead />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {rows.map((t) => {
                            const isCapex = t._source === "capex";
                            const typeClass = t.type === "income" ? "bg-primary/10 text-primary" : isCapex ? "bg-amber-500/10 text-amber-700" : "bg-accent/10 text-accent";
                            return (
                                <TableRow key={t.id} data-testid={isCapex ? `financial-capex-row-${t._capexId}` : `financial-tx-row-${t.id}`}>
                                    <TableCell className="font-mono">{t.date}</TableCell>
                                    <TableCell><span className={`px-2 py-1 rounded-md text-xs ${typeClass}`}>{isCapex ? "capex" : t.type}</span></TableCell>
                                    <TableCell>{t.category}{isCapex && <Badge variant="outline" className="ml-2 text-xs">{i18n.t("batch3.da33e1b7e0c4")}</Badge>}</TableCell>
                                    <TableCell className="max-w-xs truncate">{t.description}</TableCell>
                                    <TableCell className="text-right font-mono font-semibold">{Number(t.amount).toFixed(2)}</TableCell>
                                    <TableCell className="text-right">
                                        {isCapex
                                            ? <a href="/construction" className="text-xs text-muted-foreground underline hover:text-primary">{i18n.t("batch3.bfa0df1d1c50")}</a>
                                            : <Button size="sm" variant="destructive" onClick={() => del(t)}>{i18n.t("batch3.f6fdbe48dc54")}</Button>}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">{i18n.t("batch3.f794b6c6d153")}</TableCell></TableRow>}
                    </TableBody>
                </Table>
            </Card>
        </div>
    );
}
